import { ReportType } from './reports';
import { LocaleCode, MarketCode } from './markets';

export const VERSIONS = ['0.4', '0.5', '1.0'];

export const PUBLIC_VERSION = '1.0';

export type BuildLinkUrlParams = {
  clientId: string;
  redirectUri: string;
  scope?: string | string[]; // TODO: use Scope
  market: MarketCode;
  locale: LocaleCode;
  host: string;
  version?: string;
  product?: string;
  debug?: boolean;
  iframe?: boolean;
  paymentRequestId?: string;
  inputProvider?: string;
  inputUsername?: string;
  externalReference?: string;
  products?: string;
  state?: string;
  authorisationCode?: string;
  consentId?: string;
  async?: boolean;
  reportTypes?: Array<ReportType>;
};

export const buildLinkUrl = ({
  clientId,
  redirectUri,
  scope,
  market,
  locale,
  host = 'https://link.tink.com',
  version = PUBLIC_VERSION,
  product = 'authorize',
  debug = false,
  iframe = false,
  paymentRequestId,
  inputProvider,
  inputUsername,
  externalReference,
  products,
  state,
  authorisationCode,
  consentId,
  async,
  reportTypes,
}: BuildLinkUrlParams) => {
  const extraParam = products ? `${products}&` : ''; // this is new for assets & liabilities
  let fullUrl = `${host}/${version}/${product}/?${extraParam}client_id=${clientId}&redirect_uri=${redirectUri}&market=${market}&locale=${locale}`;

  if (scope) {
    fullUrl += `&scope=${scope}`;
  }

  if (iframe) {
    fullUrl += '&iframe=true';
  }

  if (debug) {
    fullUrl += '&debug=true';
  }

  if (paymentRequestId) {
    fullUrl += `&payment_request_id=${paymentRequestId}`;
  }

  if (inputProvider) {
    fullUrl += `&input_provider=${inputProvider}`;
  }

  if (inputUsername) {
    fullUrl += `&input_username=${inputUsername}`;
  }

  if (externalReference) {
    fullUrl += `&external_reference=${encodeURIComponent(externalReference)}`;
  }

  if (state) {
    fullUrl += `&state=${state}`;
  }

  if (consentId) {
    fullUrl += `&consent_id=${consentId}`;
  }

  if (authorisationCode) {
    fullUrl += `&authorisation_code=${authorisationCode}`;
  }

  if (reportTypes) {
    fullUrl += `&report_types=${reportTypes.join(',')}`;
  }

  if (async) {
    fullUrl += `&async=true`;
  }

  return fullUrl;
};

export enum ConsentType {
  FETCH = 'FETCH',
  FETCH_AND_DISPLAY = 'FETCH_AND_DISPLAY',
}

export enum ThemeCode {
  DARK = 'DARK',
  LIGHT = 'LIGHT',
}

export type ConfigurationColors = {
  primaryColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  buttonSecondaryTextColor: string;
  backgroundColor: string;
  tintedBackgroundColor: string;
  errorTextColor: string;
  pageBackgroundColor: string;
  strokeColor: string;
};

export enum BorderRadius {
  PILL = 'PILL',
  ROUNDED = 'ROUNDED',
  SHARP = 'SHARP',
}

export enum TextWeight {
  BOLD = 'BOLD',
  NORMAL = 'NORMAL',
}

export type ConfigurationButtons = {
  textWeight: TextWeight;
  borderRadius: BorderRadius;
  fullWidth: boolean;
  textUppercase: boolean;
  shadow: boolean;
};

export type FontSizeUnit = 'px' | 'rem';

export type FontSize = `${number}${FontSizeUnit}`;

export type Typography = {
  fontSize: FontSize;
  lineHeight: number | `${number}px`;
};

export type ConfigurationTypography = {
  h1: Typography;
  h2: Typography;
  paragraph: Typography;
  caption: Typography;
};

export type ConfigurationThemeResponse = {
  id: string;
  colors: ConfigurationColors;
  buttons: ConfigurationButtons;
  header: ConfigurationHeader;
  fonts: {
    fontFamily: string;
    provider: string;
    identifier: string;
    sourceUrl: string[];
    sourceFont: string[];
  };
  typography: ConfigurationTypography;
  type: ThemeCode;
  logo: ConfigurationThemeAsset;
};

export enum LogoAlign {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
}

export type ConfigurationHeader = {
  logoAlign: LogoAlign;
  showBackButton: boolean;
  showCloseButton: boolean;
};

export type ConfigurationTheme = ConfigurationThemeResponse & {
  textAlignment: TextAlignment;
};

export type ConfigurationThemeAsset = {
  file: string;
  url: string;
};

export type ConfigurationModel = {
  id: string;
  featureFlags: ConfigurationFeatureFlag[];
  consentType: ConsentType;
};

export enum ConfigurationFeatureFlag {
  TINK_LOGO_HIDDEN = 'TINK_LOGO_HIDDEN',
  CREDENTIAL_TERMS_HIDDEN = 'CREDENTIAL_TERMS_HIDDEN',
  CREDENTIAL_SCOPES_HIDDEN = 'CREDENTIAL_SCOPES_HIDDEN',
  SKIP_FINAL_SUCCESS_SCREEN = 'SKIP_FINAL_SUCCESS_SCREEN',
  ENABLE_SUCCESS_SCREEN_AC = 'ENABLE_SUCCESS_SCREEN_AC',
  ENABLE_SUCCESS_SCREEN_IC = 'ENABLE_SUCCESS_SCREEN_IC',
  ENABLE_SUCCESS_SCREEN_RI = 'ENABLE_SUCCESS_SCREEN_RI',
  SKIP_UPDATING_CREDENTIALS = 'SKIP_UPDATING_CREDENTIALS',
  SKIP_USER_IDENTIFICATION = 'SKIP_USER_IDENTIFICATION',
  REDIRECT_BACK_TO_CLIENT_BUTTON_HIDDEN = 'REDIRECT_BACK_TO_CLIENT_BUTTON_HIDDEN',
  UPPERCASE_BUTTON_TEXT = 'UPPERCASE_BUTTON_TEXT',
  NO_SHADOW_BUTTON = 'NO_SHADOW_BUTTON',
  SKIP_CONSENT_SCREEN = 'SKIP_CONSENT_SCREEN',
  FALLBACK_TO_PROVIDER_SELECTION = 'FALLBACK_TO_PROVIDER_SELECTION',
  DISABLE_HANDOFF = 'DISABLE_HANDOFF',
  REDIRECT_TO_IFRAME_REFER_PAGE = 'REDIRECT_TO_IFRAME_REFER_PAGE',
  HIDE_LOADING_MESSAGES = 'HIDE_LOADING_MESSAGES',
  ENABLE_EXIT_FEEDBACK = 'ENABLE_EXIT_FEEDBACK',
  SHOW_CLIENT_LEGAL_DOCS = 'SHOW_CLIENT_LEGAL_DOCS', // both custom footer text and custom links
  SHOW_APP_SPECIFIC_LEGAL_DOCS = 'SHOW_APP_SPECIFIC_LEGAL_DOCS', // custom links only
  REJECT_DOUBLE_PAYMENT = 'REJECT_DOUBLE_PAYMENT',
  REJECT_FAILED_OR_CANCELLED = 'REJECT_FAILED_OR_CANCELLED',
  REJECT_PAYMENT_IF_INSUFFICIENT_FUNDS = 'REJECT_PAYMENT_IF_INSUFFICIENT_FUNDS',
  REDIRECT_USER_ON_MOBILE_DEVICE_TO_THIRD_PARTY_AUTOMATICALLY = 'REDIRECT_USER_ON_MOBILE_DEVICE_TO_THIRD_PARTY_AUTOMATICALLY',
  RESET_TRANSIENT_CREDENTIALS_STATUS = 'RESET_TRANSIENT_CREDENTIALS_STATUS',
  ENABLE_PAYMENTS_WITH_FTS = 'ENABLE_PAYMENTS_WITH_FTS',
  SLOW_CREDENTIALS = 'SLOW_CREDENTIALS',
  PAYMENT_ACCOUNT_CHECK_BUNDLE = 'PAYMENT_ACCOUNT_CHECK_BUNDLE',
  PAYMENT_FRAUD_PREVENTION_BUNDLE = 'PAYMENT_FRAUD_PREVENTION_BUNDLE',
  HIDE_PROVIDER_HEALTH_SCREENS = 'HIDE_PROVIDER_HEALTH_SCREENS',
  /**
    Navigate users back to original Tink Link instance once authentication process in 3rd party is finished.
    More in https://tinkab.atlassian.net/browse/COREX-84
   */
  NAVIGATE_USER_TO_INITIAL_TINK_LINK_AFTER_3RD_PARTY_REDIRECT = 'NAVIGATE_USER_TO_INITIAL_TINK_LINK_AFTER_3RD_PARTY_REDIRECT',
  /**
   * Temporary feature flag to enable `data/v2/accounts` endpoint instead of `api/v1/transfer/accounts`
   * Used to show available account balance or to use account type filtering functionality
   */
  ENABLE_ACCOUNTS_V2_ENDPOINT = 'ENABLE_ACCOUNTS_V2_ENDPOINT',
  /**
   * Temporary feature flag to for internal testing
   * of new configuration to the skip AIS
   */
  TEST_SKIPPING_AIS_CONFIGURATION_CHANGE = 'TEST_SKIPPING_AIS_CONFIGURATION_CHANGE',
  /**
   * temporary feature flag for development purposes
   */
  ENABLE_BULK_PAYMENTS = 'ENABLE_BULK_PAYMENTS',
  /* * Temporary feature flag to for internal testing
   * of new configuration for permanent user 1SCA
   */
  PERMANENT_USER_1SCA = 'PERMANENT_USER_1SCA',
  /**
   * Enable QR Handoff even if provider is blocklisted (Monzo, Revolut etc.)
   */
  ENABLE_BLOCKLISTED_HANDOFF = 'ENABLE_BLOCKLISTED_HANDOFF',
  /**
   * Errors should be redirected whenever possible.
   * As opposite of show them in Tink Link.
   */
  ERRORS_REDIRECT = 'ERRORS_REDIRECT',
  REJECT_MULTIPLE_TRANSFERS = 'REJECT_MULTIPLE_TRANSFERS',
  /**
   * Hide back button only when input_provider is specified (working currently only in PIS Handoff flow)
   */
  HIDE_BACK_BUTTON_WHEN_PROVIDER_SPECIFIED = 'HIDE_BACK_BUTTON_WHEN_PROVIDER_SPECIFIED',
  /**
   * Will terminate the flow and redirect user back to client if no account where found in AccountSelectionsDirector
   */
  TERMINATE_FLOW_ON_NO_ACCOUNTS = 'TERMINATE_FLOW_ON_NO_ACCOUNTS',
  /**
   * Will enable ConsentScreen flow
   */
  OPT_IN_CONSENT_FLOW = 'OPT_IN_CONSENT_FLOW',
  /**
   * Will enable NetflixConsentScreen flow
   */
  MERCHANT_OPT_IN_NETFLIX_CONSENT = 'MERCHANT_OPT_IN_NETFLIX_CONSENT',
  /**
   * Temporarily ff for netflix, until merchant customization is ready
   */
  MERCHANT_NETFLIX = 'MERCHANT_NETFLIX',
  /**
   * In case of two SCAs, this flag is used to auto redirect
   * from the second SCA screen to the third party.
   * See https://tinkab.atlassian.net/browse/SDK-3052.
   */
  REDIRECT_SECOND_SCA_TO_THIRD_PARTY_AUTOMATICALLY = 'REDIRECT_SECOND_SCA_TO_THIRD_PARTY_AUTOMATICALLY',
  /**
   * Feature Flag that indicates if multibanking is enabled.
   */
  MULTIBANKING = 'MULTIBANKING',
  ENABLE_DEMOBANK_SCENARIOS = 'ENABLE_DEMOBANK_SCENARIOS',
}

export type TinkLinkThemeConfiguration = {
  [key in keyof ConfigurationColors]: string;
} & { buttons: ConfigurationButtons } & {
  header: ConfigurationHeader;
  textAlignment: TextAlignment;
};

const lightThemeDefaultColors = {
  primaryColor: '#427783',
  primaryTextColor: '#262626',
  secondaryTextColor: '#707070',
  buttonBackgroundColor: '#427783',
  buttonTextColor: '#FFFFFF',
  buttonSecondaryTextColor: '#427783',
  backgroundColor: '#FFFFFF',
  tintedBackgroundColor: '#F8FBFB',
  errorTextColor: '#EA544A',
  pageBackgroundColor: '#F5F8F9',
  strokeColor: '#ECECEC',
};
const darkThemeDefaultColors = {
  primaryColor: '#5CA6B7',
  primaryTextColor: '#FFFFFF',
  secondaryTextColor: '#ABABAB',
  buttonBackgroundColor: '#427783',
  buttonTextColor: '#FFFFFF',
  buttonSecondaryTextColor: '#5CA6B7',
  backgroundColor: '#262626',
  tintedBackgroundColor: '#4D4D4D',
  errorTextColor: '#EA544A',
  pageBackgroundColor: '#000000',
  strokeColor: '#535353',
};

const defaultButtonConfig: ConfigurationButtons = {
  textWeight: TextWeight.BOLD,
  borderRadius: BorderRadius.PILL,
  fullWidth: false,
  textUppercase: false,
  shadow: true,
};

export enum TextAlignment {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
}

export const getDefaultConfigurationTheme = (themeCode: ThemeCode): ConfigurationTheme => ({
  id: 'default',
  colors: themeCode === ThemeCode.DARK ? darkThemeDefaultColors : lightThemeDefaultColors,
  buttons: defaultButtonConfig,
  fonts: {
    fontFamily:
      "-apple-system,BlinkMacSystemFont,'Segoe',Roboto,Helvetica,Arial,sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol'",
    provider: '',
    identifier: '',
    sourceUrl: [],
    sourceFont: [],
  },
  type: ThemeCode.LIGHT,
  logo: {
    file: '',
    url: '',
  },
  header: {
    logoAlign: LogoAlign.CENTER,
    showBackButton: true,
    showCloseButton: true,
  },
  typography: {
    h1: {
      fontSize: '1.5rem', // medium
      lineHeight: 1.5,
    },
    h2: {
      fontSize: '1rem', // normal
      lineHeight: 1.25,
    },
    paragraph: {
      fontSize: '1rem', // normal
      lineHeight: 1.25,
    },
    caption: {
      fontSize: '0.875rem', // small
      lineHeight: 1.25,
    },
  },
  textAlignment: TextAlignment.LEFT,
});

export const defaultConfiguration: ConfigurationModel = {
  id: 'default',
  featureFlags: [],
  consentType: ConsentType.FETCH,
};

export const getPrefilledButtons = (buttons: ConfigurationButtons) => {
  const buttonConfigClone = { ...buttons };

  // override wrong values with default
  if (buttonConfigClone.borderRadius.startsWith('UNKNOWN')) {
    buttonConfigClone.borderRadius = defaultButtonConfig.borderRadius;
  }

  if (buttonConfigClone.textWeight.startsWith('UNKNOWN')) {
    buttonConfigClone.textWeight = defaultButtonConfig.textWeight;
  }

  return buttonConfigClone;
};

export type PreviewMessage = {
  theme: ConfigurationTheme;
};

export type PreviewMessageReady = {
  ready: boolean;
};

export const parsePreviewMessage = (message: string): PreviewMessage | null => {
  try {
    const data = JSON.parse(message) as Partial<PreviewMessage>;

    if (!data.theme) {
      return null;
    }

    if (!data.theme.colors) {
      return null;
    }

    if (!data.theme.fonts) {
      return null;
    }

    if (!data.theme.type) {
      return null;
    }

    return {
      theme: data.theme,
    };
  } catch (e) {
    return null;
  }
};
